<template>
  <section
    v-if="visible"
    class="mobile-update"
    :class="{ 'mobile-update--compact': compact }"
    aria-label="APP 更新"
  >
    <div role="status">
      <strong
        ><i class="ri-refresh-line" aria-hidden="true" /> APP 更新<span v-if="state">
          · v{{ state.currentVersion }}</span
        ></strong
      >
      <p v-if="description" :class="{ 'mobile-update__message': store.error || state?.message }">
        {{ description }}
      </p>
      <p v-if="state?.releaseNotes && !compact" class="mobile-update__notes">
        {{ state.releaseNotes }}
      </p>
      <a-progress
        v-if="state?.phase === 'downloading'"
        :percent="state.percent"
        size="small"
        aria-label="更新下载进度"
      />
    </div>
    <label v-if="state && !compact && state.phase !== 'disabled'" class="mobile-update__preference">
      <span>自动下载更新<small>使用当前网络，可能产生流量费用</small></span>
      <a-switch
        :checked="state.autoDownload"
        aria-label="自动下载更新"
        @change="store.setAutoDownload(Boolean($event))"
      />
    </label>
    <a-space wrap>
      <a-button
        v-if="!compact && state?.phase !== 'disabled'"
        aria-label="检查更新"
        :disabled="store.busy || state?.phase === 'downloaded'"
        :loading="state?.phase === 'checking'"
        @click="store.check()"
        >检查更新</a-button
      >
      <a-button
        v-if="state?.latestVersion && ['available', 'error'].includes(state.phase)"
        aria-label="下载更新"
        type="primary"
        @click="store.run('download')"
        >{{ state.phase === 'error' ? '重试下载' : '下载更新' }}</a-button
      >
      <a-button
        v-if="state?.phase === 'downloaded'"
        aria-label="安装更新"
        type="primary"
        @click="store.run('install')"
        >安装更新</a-button
      >
      <a-button
        v-if="compact"
        aria-label="稍后更新"
        @click="store.dismissedVersion = state?.latestVersion ?? ''"
        >稍后</a-button
      >
    </a-space>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useMobileUpdateStore } from '@/stores/mobile-update.store';
const props = defineProps<{ compact?: boolean }>();
const store = useMobileUpdateStore();
const state = computed(() => store.state);
const visible = computed(
  () =>
    store.available &&
    (!props.compact ||
      (!!state.value?.latestVersion &&
        ['available', 'downloaded'].includes(state.value.phase) &&
        store.dismissedVersion !== state.value.latestVersion)),
);
const description = computed(() => {
  const value = state.value;
  const message = store.error || value?.message;
  if (message) return message;
  if (!value) return '正在获取版本…';
  switch (value.phase) {
    case 'checking':
      return '正在检查更新…';
    case 'available':
      return `发现新版本 v${value.latestVersion}`;
    case 'downloading':
      return `正在下载 v${value.latestVersion}`;
    case 'downloaded':
      return `v${value.latestVersion} 已下载，保存内容后安装`;
    case 'error':
      return '更新失败，请重试';
    default:
      return value.lastCheckedAt ? '已是最新版本' : '';
  }
});
</script>

<style scoped>
.mobile-update {
  display: grid;
  gap: 14px;
  padding: 16px;
  margin-top: 20px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-lg);
  background: var(--aw-surface);
}
.mobile-update p {
  margin: 6px 0 0;
  color: var(--aw-text-secondary);
  overflow-wrap: anywhere;
}
.mobile-update__notes {
  white-space: pre-wrap;
}
.mobile-update .mobile-update__message {
  color: var(--aw-warning);
}
.mobile-update__preference {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.mobile-update small {
  display: block;
  margin-top: 4px;
  color: var(--aw-text-secondary);
}
.mobile-update :deep(.ant-btn) {
  min-height: 44px;
}
.mobile-update--compact {
  margin: 0;
  border-radius: 0;
  padding-top: max(12px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px)));
}
</style>
